import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Popover, MenuItem } from '@material-ui/core';

const CustomChart = ({ 
  title, 
  data, 
  width = 400, 
  height = 300, 
  color = '#ff9800',
  showGrid = true,
  showPoints = true,
  lineWidth = 2,
  chartType = 'line', 
  pointSize = 3,
  lineStyle = 'solid', 
  backgroundColor = '#fafafa',
  chartBackgroundColor = '#fff',
  multiLine = false,
  onPointClick = null
}) => {


  const canvasRef = useRef(null);
  const [zoomState, setZoomState] = useState({
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null,
    isZoomed: false
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragEnd, setDragEnd] = useState({ x: 0, y: 0 });
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [clickedPointData, setClickedPointData] = useState(null);
  
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { points: [], xRange: [0, 1], yRange: [0, 1] };
    
    if (multiLine) {
      // Handle multiple lines
      const allPoints = [];
      const allXValues = [];
      const allYValues = [];
      
      data.forEach(lineData => {
        if (lineData.points && lineData.points.length > 0) {
          const points = lineData.points.map(([x, y]) => ({ x, y }));
          allPoints.push({ ...lineData, points });
          allXValues.push(...points.map(p => p.x));
          allYValues.push(...points.map(p => p.y));
        }
      });
      
      if (allXValues.length === 0) return { points: [], xRange: [0, 1], yRange: [0, 1] };
      
      const xRange = [Math.min(...allXValues), Math.max(...allXValues)];
      const yRange = [Math.min(...allYValues), Math.max(...allYValues)];
      
      // Add some padding to y range
      let yPadding = (yRange[1] - yRange[0]) * 0.1;
      // If all values are the same, create a minimum range around the value
      if (yRange[0] === yRange[1]) {
        const value = yRange[0];
        yPadding = Math.max(Math.abs(value) * 0.1, 1); // At least 1 unit or 10% of value
        yRange[0] = Math.max(0, value - yPadding);
        yRange[1] = value + yPadding;
      } else {
        yRange[0] = Math.max(0, yRange[0] - yPadding);
        yRange[1] = yRange[1] + yPadding;
      }
      
      // For single point, ensure minimum range
      if (allXValues.length === 1) {
        const x = allXValues[0];
        const y = allYValues[0];
        xRange[0] = x - 86400000; // 1 day before
        xRange[1] = x + 86400000; // 1 day after
        yRange[0] = Math.max(0, y - y * 0.1);
        yRange[1] = y + y * 0.1;
      }
      
      return { points: allPoints, xRange, yRange, multiLine: true };
    } else {
      // Handle single line (backward compatibility)
      const points = data.map(([x, y]) => ({ x, y }));
      const xValues = points.map(p => p.x);
      const yValues = points.map(p => p.y);
      
      const xRange = [Math.min(...xValues), Math.max(...xValues)];
      const yRange = [Math.min(...yValues), Math.max(...yValues)];
      
      // Add some padding to y range
      let yPadding = (yRange[1] - yRange[0]) * 0.1;
      // If all values are the same, create a minimum range around the value
      if (yRange[0] === yRange[1]) {
        const value = yRange[0];
        yPadding = Math.max(Math.abs(value) * 0.1, 1); // At least 1 unit or 10% of value
        yRange[0] = Math.max(0, value - yPadding);
        yRange[1] = value + yPadding;
      } else {
        yRange[0] = Math.max(0, yRange[0] - yPadding);
        yRange[1] = yRange[1] + yPadding;
      }
      
      // For single point, ensure minimum range
      if (xValues.length === 1) {
        const x = xValues[0];
        const y = yValues[0];
        xRange[0] = x - 86400000; // 1 day before
        xRange[1] = x + 86400000; // 1 day after
        yRange[0] = Math.max(0, y - y * 0.1);
        yRange[1] = y + y * 0.1;
      }
      
      return { points, xRange, yRange, multiLine: false };
    }
  }, [data, multiLine]);

  // Get current display ranges (with zoom applied)
  const getDisplayRanges = () => {
    if (zoomState.isZoomed) {
      return {
        xRange: [zoomState.xMin, zoomState.xMax],
        yRange: [zoomState.yMin, zoomState.yMax]
      };
    }
    return chartData;
  };

  // Convert screen coordinates to data coordinates
  const screenToData = (screenX, screenY) => {
    const padding = { top: 40, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const { xRange, yRange } = getDisplayRanges();
    
    const dataX = xRange[0] + ((screenX - padding.left) / chartWidth) * (xRange[1] - xRange[0]);
    const dataY = yRange[1] - ((screenY - padding.top) / chartHeight) * (yRange[1] - yRange[0]);
    
    return { x: dataX, y: dataY };
  };

  // Find the closest point to clicked coordinates
  const findClosestPoint = useCallback((clickX, clickY) => {
    if (!chartData.points || chartData.points.length === 0) {
      return null;
    }
    
    // Use same padding as in drawing code
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const { xRange, yRange } = getDisplayRanges();
    
    const transformX = (x) => {
      return padding.left + ((x - xRange[0]) / (xRange[1] - xRange[0])) * chartWidth;
    };

    const transformY = (y) => {
      return padding.top + chartHeight - ((y - yRange[0]) / (yRange[1] - yRange[0])) * chartHeight;
    };
    
    let closestPoint = null;
    let minDistance = Infinity;
    const clickThreshold = 20; // pixels
    
    if (multiLine && chartData.multiLine) {
      chartData.points.forEach((lineData, lineIndex) => {
        if (!lineData.points || lineData.points.length === 0) return;
        lineData.points.forEach((point, pointIndex) => {
          const x = transformX(point.x);
          const y = transformY(point.y);
          const distance = Math.sqrt(
            Math.pow(clickX - x, 2) + Math.pow(clickY - y, 2)
          );
          
            if (distance < minDistance && distance < clickThreshold) {
              minDistance = distance;
              closestPoint = { x: point.x, y: point.y, color: lineData.color };
            }
        });
      });
    } else {
      const points = chartData.points;
      points.forEach((point, index) => {
        const x = transformX(point.x);
        const y = transformY(point.y);
        const distance = Math.sqrt(
          Math.pow(clickX - x, 2) + Math.pow(clickY - y, 2)
        );
        
        if (distance < minDistance && distance < clickThreshold) {
          minDistance = distance;
          closestPoint = { x: point.x, y: point.y, color };
        }
      });
    }
    
    return closestPoint;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, multiLine, width, height, color]);

  // Find hovered point (similar to findClosestPoint but with smaller threshold for hover)
  const findHoveredPoint = useCallback((mouseX, mouseY) => {
    if (!chartData.points || chartData.points.length === 0) {
      return null;
    }
    
    // Use same padding as in drawing code
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const { xRange, yRange } = getDisplayRanges();
    
    const transformX = (x) => {
      return padding.left + ((x - xRange[0]) / (xRange[1] - xRange[0])) * chartWidth;
    };

    const transformY = (y) => {
      return padding.top + chartHeight - ((y - yRange[0]) / (yRange[1] - yRange[0])) * chartHeight;
    };
    
    // Use mouse coordinates directly (no adjustment needed)
    const adjustedMouseX = mouseX;
    const adjustedMouseY = mouseY;
    
    let hoveredPoint = null;
    let minDistance = Infinity;
    const hoverThreshold = 15; // pixels - smaller threshold for hover
    
    if (multiLine && chartData.multiLine) {
      chartData.points.forEach((lineData) => {
        if (!lineData.points || lineData.points.length === 0) return;
        lineData.points.forEach((point) => {
          const x = transformX(point.x);
          const y = transformY(point.y);
          const distance = Math.sqrt(
            Math.pow(adjustedMouseX - x, 2) + Math.pow(adjustedMouseY - y, 2)
          );
          
          if (distance < minDistance && distance < hoverThreshold) {
            minDistance = distance;
            hoveredPoint = { x: point.x, y: point.y, color: lineData.color };
          }
        });
      });
    } else {
      const points = chartData.points;
      points.forEach((point) => {
        const x = transformX(point.x);
        const y = transformY(point.y);
        const distance = Math.sqrt(
          Math.pow(adjustedMouseX - x, 2) + Math.pow(adjustedMouseY - y, 2)
        );
        
        if (distance < minDistance && distance < hoverThreshold) {
          minDistance = distance;
          hoveredPoint = { x: point.x, y: point.y, color };
        }
      });
    }
    
    return hoveredPoint;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, multiLine, width, height, color]);

  // Handle mouse move for hover effect
  const handleMouseMoveHover = useCallback((event) => {
    if (!onPointClick || isDragging) {
      setHoveredPoint(null);
      return;
    }
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    
    // Calculate mouse coordinates relative to canvas
    // Use CSS dimensions directly (canvas.style.width/height, not canvas.width/height)
    // This accounts for any CSS scaling that might occur
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const mouseX = (event.clientX - rect.left) * scaleX;
    const mouseY = (event.clientY - rect.top) * scaleY;
    
    const hovered = findHoveredPoint(mouseX, mouseY);
    setHoveredPoint(hovered);
  }, [onPointClick, isDragging, findHoveredPoint, width, height]);

  // Handle mouse leave - clear hover
  const handleMouseLeave = useCallback(() => {
    setHoveredPoint(null);
  }, []);

  // Handle click on chart - show popover instead of directly navigating
  const handleChartClick = useCallback((event) => {
    if (!onPointClick) {
      return;
    }
    
    // Check if we have points - for multiLine check nested points
    let hasPoints = false;
    if (chartData.multiLine && chartData.points) {
      hasPoints = chartData.points.some(lineData => lineData.points && lineData.points.length > 0);
    } else if (chartData.points) {
      hasPoints = Array.isArray(chartData.points) && chartData.points.length > 0;
    }
    
    if (!hasPoints) {
      return;
    }
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    
    // Calculate click coordinates relative to canvas
    // Use CSS dimensions with scaling to account for any CSS scaling
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const clickX = (event.clientX - rect.left) * scaleX;
    const clickY = (event.clientY - rect.top) * scaleY;
    
    const closestPoint = findClosestPoint(clickX, clickY);
    let pointData = null;
    
    if (closestPoint) {
      pointData = closestPoint;
    } else {
      // Fallback: convert click coordinates to data coordinates and use that
      const { x: dataX, y: dataY } = screenToData(clickX, clickY);
      pointData = { x: dataX, y: dataY };
    }
    
    // Store clicked point data and show popover
    setClickedPointData(pointData);
    // Create a temporary element at click position for popover anchor
    const anchorElement = document.createElement('div');
    anchorElement.style.position = 'fixed';
    anchorElement.style.top = `${event.clientY}px`;
    anchorElement.style.left = `${event.clientX}px`;
    anchorElement.style.width = '1px';
    anchorElement.style.height = '1px';
    document.body.appendChild(anchorElement);
    setPopoverAnchor(anchorElement);
  }, [onPointClick, findClosestPoint, chartData, screenToData, width, height]);
  
  // Handle closing popover
  const handleClosePopover = useCallback(() => {
    setPopoverAnchor((prevAnchor) => {
      if (prevAnchor && prevAnchor.parentNode) {
        prevAnchor.parentNode.removeChild(prevAnchor);
      }
      return null;
    });
    setClickedPointData(null);
  }, []);
  
  // Handle "Open in new tab" option
  const handleOpenInNewTab = useCallback(() => {
    if (clickedPointData && onPointClick) {
      onPointClick(clickedPointData);
    }
    handleClosePopover();
  }, [clickedPointData, onPointClick, handleClosePopover]);
  

  // Handle mouse wheel zoom
  const handleWheel = (event) => {
    event.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    const { x: dataX, y: dataY } = screenToData(mouseX, mouseY);
    const zoomFactor = event.deltaY > 0 ? 1.2 : 0.8;
    
    const { xRange, yRange } = getDisplayRanges();
    
    let newXRange = xRange;
    let newYRange = yRange;
    
    if (event.ctrlKey) {
      // Zoom Y axis only
      newYRange = [
        dataY - (dataY - yRange[0]) * zoomFactor,
        dataY + (yRange[1] - dataY) * zoomFactor
      ];
    } else if (event.shiftKey) {
      // Zoom X axis only
      newXRange = [
        dataX - (dataX - xRange[0]) * zoomFactor,
        dataX + (xRange[1] - dataX) * zoomFactor
      ];
    } else {
      // Zoom both axes
      newXRange = [
        dataX - (dataX - xRange[0]) * zoomFactor,
        dataX + (xRange[1] - dataX) * zoomFactor
      ];
      newYRange = [
        dataY - (dataY - yRange[0]) * zoomFactor,
        dataY + (yRange[1] - dataY) * zoomFactor
      ];
    }
    
    // Apply zoom limits
    const originalXRange = chartData.xRange;
    const originalYRange = chartData.yRange;
    
    const maxZoomFactor = 100; // Maximum zoom factor
    const minZoomFactor = 0.01; // Minimum zoom factor
    
    const xRangeSize = originalXRange[1] - originalXRange[0];
    const yRangeSize = originalYRange[1] - originalYRange[0];
    
    const minXRangeSize = xRangeSize / maxZoomFactor;
    const maxXRangeSize = xRangeSize / minZoomFactor;
    const minYRangeSize = yRangeSize / maxZoomFactor;
    const maxYRangeSize = yRangeSize / minZoomFactor;
    
    const newXRangeSize = newXRange[1] - newXRange[0];
    const newYRangeSize = newYRange[1] - newYRange[0];
    
    // Clamp ranges to limits
    if (newXRangeSize < minXRangeSize) {
      const center = (newXRange[0] + newXRange[1]) / 2;
      newXRange[0] = center - minXRangeSize / 2;
      newXRange[1] = center + minXRangeSize / 2;
    } else if (newXRangeSize > maxXRangeSize) {
      const center = (newXRange[0] + newXRange[1]) / 2;
      newXRange[0] = center - maxXRangeSize / 2;
      newXRange[1] = center + maxXRangeSize / 2;
    }
    
    if (newYRangeSize < minYRangeSize) {
      const center = (newYRange[0] + newYRange[1]) / 2;
      newYRange[0] = center - minYRangeSize / 2;
      newYRange[1] = center + minYRangeSize / 2;
    } else if (newYRangeSize > maxYRangeSize) {
      const center = (newYRange[0] + newYRange[1]) / 2;
      newYRange[0] = center - maxYRangeSize / 2;
      newYRange[1] = center + maxYRangeSize / 2;
    }
    
    const newZoomState = {
      xMin: newXRange[0],
      xMax: newXRange[1],
      yMin: newYRange[0],
      yMax: newYRange[1],
      isZoomed: true
    };
    
    setZoomState(newZoomState);
  };

  // Handle mouse down for panning
  const handleMouseDown = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setIsDragging(true);
    setDragStart({ x, y });
    setDragEnd({ x, y });
  };

  // Handle mouse move for panning
  const handleMouseMove = (event) => {
    if (!isDragging) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setDragEnd({ x, y });
  };

  // Handle mouse up for panning
  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const deltaX = dragEnd.x - dragStart.x;
    const deltaY = dragEnd.y - dragStart.y;
    
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      const { xRange, yRange } = getDisplayRanges();
      const padding = { top: 40, right: 20, bottom: 40, left: 50 };
      const chartWidth = width - padding.left - padding.right;
      const chartHeight = height - padding.top - padding.bottom;
      
      const xRangeDelta = (deltaX / chartWidth) * (xRange[1] - xRange[0]);
      const yRangeDelta = (deltaY / chartHeight) * (yRange[1] - yRange[0]);
      
      const newZoomState = {
        xMin: xRange[0] - xRangeDelta,
        xMax: xRange[1] - xRangeDelta,
        yMin: yRange[0] + yRangeDelta,
        yMax: yRange[1] + yRangeDelta,
        isZoomed: true
      };
      
      setZoomState(newZoomState);
    }
    
    setIsDragging(false);
  };

  // Reset zoom
  const resetZoom = () => {
    if (!zoomState.isZoomed) return;
    
    setZoomState({
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null,
      isZoomed: false
    });
  };



  // Double click to reset zoom
  const handleDoubleClick = () => {
    resetZoom();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !chartData.points.length) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas size accounting for device pixel ratio
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    const { points } = chartData;
    const { xRange, yRange } = getDisplayRanges();
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Helper function to convert data coordinates to canvas coordinates
    const transformX = (x) => {
      return padding.left + ((x - xRange[0]) / (xRange[1] - xRange[0])) * chartWidth;
    };

    const transformY = (y) => {
      return padding.top + chartHeight - ((y - yRange[0]) / (yRange[1] - yRange[0])) * chartHeight;
    };



    // Draw background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    
    // Draw chart area background
    ctx.fillStyle = chartBackgroundColor;
    ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);
    
    // Set clipping region for chart area
    ctx.save();
    ctx.beginPath();
    ctx.rect(padding.left, padding.top, chartWidth, chartHeight);
    ctx.clip();

    // Draw title
    if (title) {
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 14px Open Sans, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(title, width / 2, 20);
    }

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#f5f5f5';
      ctx.lineWidth = 0.3;
      
      // Vertical grid lines (time axis)
      const timeStep = (xRange[1] - xRange[0]) / 5;
      for (let i = 0; i <= 5; i++) {
        const x = xRange[0] + i * timeStep;
        const canvasX = transformX(x);
        ctx.beginPath();
        ctx.moveTo(canvasX, padding.top);
        ctx.lineTo(canvasX, height - padding.bottom);
        ctx.stroke();
      }

      // Horizontal grid lines (value axis) - more frequent for better readability
      const valueStep = (yRange[1] - yRange[0]) / 13;
      for (let i = 0; i <= 13; i++) {
        const y = yRange[0] + i * valueStep;
        const canvasY = transformY(y);
        ctx.beginPath();
        ctx.moveTo(padding.left, canvasY);
        ctx.lineTo(width - padding.right, canvasY);
        ctx.stroke();
      }
    }

    // Draw data based on chart type
    if (multiLine && chartData.multiLine) {
      // Draw multiple lines
      chartData.points.forEach(lineData => {
        const points = lineData.points;
        const lineColor = lineData.color || color;
        
        if (points.length >= 1) {
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = lineWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          // Set line style
          if (lineStyle === 'dashed') {
            ctx.setLineDash([5, 5]);
          } else if (lineStyle === 'dotted') {
            ctx.setLineDash([2, 2]);
          } else {
            ctx.setLineDash([]);
          }
          
          ctx.beginPath();
          ctx.moveTo(transformX(points[0].x), transformY(points[0].y));
          
          if (points.length === 1) {
            // For single point, draw a small horizontal line
            const x = transformX(points[0].x);
            const y = transformY(points[0].y);
            ctx.moveTo(x - 10, y);
            ctx.lineTo(x + 10, y);
          } else {
            // For multiple points, draw the line
            for (let i = 1; i < points.length; i++) {
              ctx.lineTo(transformX(points[i].x), transformY(points[i].y));
            }
          }
          
          ctx.stroke();
          ctx.setLineDash([]); // Reset line style
        }
      });
    } else if (points.length >= 1) {
      if (chartType === 'area') {
        // Draw area chart
        ctx.fillStyle = color + '40'; // Add transparency
        ctx.beginPath();
        ctx.moveTo(transformX(points[0].x), transformY(points[0].y));
        
        if (points.length === 1) {
          // For single point, create a small area
          const x = transformX(points[0].x);
          const y = transformY(points[0].y);
          ctx.lineTo(x + 10, y);
          ctx.lineTo(x + 10, height - padding.bottom);
          ctx.lineTo(x - 10, height - padding.bottom);
          ctx.lineTo(x - 10, y);
        } else {
          // For multiple points, draw the area
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(transformX(points[i].x), transformY(points[i].y));
          }
          
          // Close the path to create a filled area
          ctx.lineTo(transformX(points[points.length - 1].x), height - padding.bottom);
          ctx.lineTo(transformX(points[0].x), height - padding.bottom);
        }
        
        ctx.closePath();
        ctx.fill();
        
        // Draw the line on top
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Set line style
        if (lineStyle === 'dashed') {
          ctx.setLineDash([5, 5]);
        } else if (lineStyle === 'dotted') {
          ctx.setLineDash([2, 2]);
        } else {
          ctx.setLineDash([]);
        }
        
        ctx.beginPath();
        ctx.moveTo(transformX(points[0].x), transformY(points[0].y));
        
        if (points.length === 1) {
          // For single point, draw a small horizontal line
          const x = transformX(points[0].x);
          const y = transformY(points[0].y);
          ctx.moveTo(x - 10, y);
          ctx.lineTo(x + 10, y);
        } else {
          // For multiple points, draw the line
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(transformX(points[i].x), transformY(points[i].y));
          }
        }
        
        ctx.stroke();
        ctx.setLineDash([]); // Reset line style
      } else if (chartType === 'scatter') {
        // Draw only points for scatter plot
        // Points are drawn separately below
      } else {
        // Default line chart
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Set line style
        if (lineStyle === 'dashed') {
          ctx.setLineDash([5, 5]);
        } else if (lineStyle === 'dotted') {
          ctx.setLineDash([2, 2]);
        } else {
          ctx.setLineDash([]);
        }
        
        ctx.beginPath();
        ctx.moveTo(transformX(points[0].x), transformY(points[0].y));
        
        // For single point, draw a small horizontal line
        if (points.length === 1) {
          const x = transformX(points[0].x);
          const y = transformY(points[0].y);
          ctx.moveTo(x - 10, y);
          ctx.lineTo(x + 10, y);
        } else {
          // For multiple points, draw the line
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(transformX(points[i].x), transformY(points[i].y));
          }
        }
        
        ctx.stroke();
        ctx.setLineDash([]); // Reset line style
      }
    }

    // Draw data points
    if (showPoints || chartType === 'scatter') {
      const finalPointSize = chartType === 'scatter' ? Math.max(pointSize, 4) : pointSize;
      
      if (multiLine && chartData.multiLine) {
        // Draw points for multiple lines
        chartData.points.forEach(lineData => {
          const points = lineData.points;
          const lineColor = lineData.color || color;
          
          points.forEach(point => {
            const x = transformX(point.x);
            const y = transformY(point.y);
            
            // Check if this point is hovered
            const isHovered = hoveredPoint && 
              Math.abs(hoveredPoint.x - point.x) < 0.001 && 
              Math.abs(hoveredPoint.y - point.y) < 0.001 &&
              (hoveredPoint.color === lineColor || !hoveredPoint.color);
            
            const pointSizeToUse = isHovered ? finalPointSize * 1.8 : finalPointSize;
            const pointColor = isHovered ? '#2196f3' : lineColor;
            
            // Draw point with white border for better visibility
            ctx.fillStyle = pointColor;
            ctx.beginPath();
            ctx.arc(x, y, pointSizeToUse, 0, 2 * Math.PI);
            ctx.fill();
            
            // Add white border (thicker for hovered)
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = isHovered ? 2.5 : 1.5;
            ctx.stroke();
            
            // Add glow effect for hovered point
            if (isHovered) {
              ctx.shadowBlur = 10;
              ctx.shadowColor = pointColor;
              ctx.beginPath();
              ctx.arc(x, y, pointSizeToUse + 2, 0, 2 * Math.PI);
              ctx.stroke();
              ctx.shadowBlur = 0;
            }
          });
        });
      } else {
        // Draw points for single line
        points.forEach(point => {
          const x = transformX(point.x);
          const y = transformY(point.y);
          
          // Check if this point is hovered
          const isHovered = hoveredPoint && 
            Math.abs(hoveredPoint.x - point.x) < 0.001 && 
            Math.abs(hoveredPoint.y - point.y) < 0.001;
          
          const pointSizeToUse = isHovered ? finalPointSize * 1.8 : finalPointSize;
          const pointColor = isHovered ? '#2196f3' : color;
          
          // Draw point with white border for better visibility
          ctx.fillStyle = pointColor;
          ctx.beginPath();
          ctx.arc(x, y, pointSizeToUse, 0, 2 * Math.PI);
          ctx.fill();
          
          // Add white border (thicker for hovered)
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = isHovered ? 2.5 : 1.5;
          ctx.stroke();
          
          // Add glow effect for hovered point
          if (isHovered) {
            ctx.shadowBlur = 10;
            ctx.shadowColor = pointColor;
            ctx.beginPath();
            ctx.arc(x, y, pointSizeToUse + 2, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.shadowBlur = 0;
          }
        });
      }
    }



    // Restore context (remove clipping)
    ctx.restore();
    
    // Draw axes (outside clipping area so they're always visible)
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1;
    
    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();
    
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, height - padding.bottom);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = '#333333';
    ctx.font = '11px Open Sans, Arial, sans-serif';
    ctx.textAlign = 'center';

    // X-axis labels (time)
    const timeStep = (xRange[1] - xRange[0]) / 5;
    for (let i = 0; i <= 5; i++) {
      const x = xRange[0] + i * timeStep;
      const canvasX = transformX(x);
      const date = new Date(x);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const label = `${day}.${month}`;
      ctx.fillText(label, canvasX, height - padding.bottom + 18);
    }

    // Y-axis labels (values)
    ctx.textAlign = 'right';
    const valueStep = (yRange[1] - yRange[0]) / 13;
    for (let i = 0; i <= 13; i++) {
      const y = yRange[0] + i * valueStep;
      const canvasY = transformY(y);
      const label = y.toFixed(1);
      ctx.fillText(label, padding.left - 8, canvasY + 4);
    }
    
    // Draw drag rectangle for panning (outside clipping area)
    if (isDragging && (Math.abs(dragEnd.x - dragStart.x) > 5 || Math.abs(dragEnd.y - dragStart.y) > 5)) {
      ctx.strokeStyle = 'rgba(0, 123, 255, 0.8)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      
      const rectX = Math.min(dragStart.x, dragEnd.x);
      const rectY = Math.min(dragStart.y, dragEnd.y);
      const rectWidth = Math.abs(dragEnd.x - dragStart.x);
      const rectHeight = Math.abs(dragEnd.y - dragStart.y);
      
      ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      
      ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      
      ctx.setLineDash([]);
    }

  }, [chartData, width, height, color, title, showGrid, showPoints, lineWidth, zoomState, chartType, pointSize, lineStyle, backgroundColor, chartBackgroundColor, hoveredPoint, multiLine]);

  // Add event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    if (onPointClick) {
      canvas.addEventListener('click', handleChartClick);
      canvas.addEventListener('mousemove', handleMouseMoveHover);
      canvas.addEventListener('mouseleave', handleMouseLeave);
    }

    // canvas.addEventListener('wheel', handleWheel, { passive: false });
    // canvas.addEventListener('mousedown', handleMouseDown);
    // canvas.addEventListener('mouseup', handleMouseUp);
    // canvas.addEventListener('dblclick', handleDoubleClick);

    // Add keyboard event listener for Escape key
    // const handleKeyDown = (event) => {
    //   if (event.key === 'Escape' && zoomState.isZoomed) {
    //     resetZoom();
    //   }
    // };

    // document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (onPointClick) {
        canvas.removeEventListener('click', handleChartClick);
        canvas.removeEventListener('mousemove', handleMouseMoveHover);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
      }
      // canvas.removeEventListener('wheel', handleWheel);
      // canvas.removeEventListener('mousedown', handleMouseDown);
      // canvas.removeEventListener('mouseup', handleMouseUp);
      // canvas.removeEventListener('dblclick', handleDoubleClick);
      // document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging, dragStart, dragEnd, zoomState, onPointClick, handleChartClick, handleMouseMoveHover, handleMouseLeave]);
  
  // Cleanup popover anchor element when component unmounts
  useEffect(() => {
    return () => {
      if (popoverAnchor && popoverAnchor.parentNode) {
        popoverAnchor.parentNode.removeChild(popoverAnchor);
      }
    };
  }, [popoverAnchor]);

      return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          style={{
            width: width,
            height: height,
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            background: chartBackgroundColor,
            // boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            cursor: onPointClick ? 'pointer' : (isDragging ? 'grabbing' : 'grab')
          }}
        />
        
        <Popover
          anchorEl={popoverAnchor}
          open={Boolean(popoverAnchor)}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableScrollLock={true}
        >
          <MenuItem onClick={handleOpenInNewTab}>
              Open chart view
          </MenuItem>
        </Popover>
      </div>
    );
};

export default CustomChart; 